import isNil from "~/kernel/validation/isNil";

type Option = {
  value: string;
  label: string;
  inputLabel?: string;
};

export type OptionsNavigatorState = {
  query: string;
  isEditMode: boolean;
  selectedIndex: number | null;
};

export type OptionsNavigatorAction =
  | { type: "input"; value: string }
  | { type: "clickOption"; option: Option }
  | { type: "blur" }
  | { type: "focus" }
  | { type: "arrowDown"; options: Option[] }
  | { type: "arrowUp"; options: Option[] }
  | { type: "enter"; options: Option[] };

function getInputDisplayValue(option: Option | undefined) {
  return option?.inputLabel ?? option?.label ?? "";
}

export function getSelectedOption(
  state: OptionsNavigatorState,
  options: Option[],
) {
  if (isNil(state.selectedIndex)) {
    return undefined;
  }

  return options[state.selectedIndex];
}

/**
 * Reducer for managing state in a suggestion dropdown such as one in a search combo box.
 **/
export default function optionsNavigatorReducer(
  state: OptionsNavigatorState,
  action: OptionsNavigatorAction,
): OptionsNavigatorState {
  const arrowSelect = (index: number, options: Option[]) => {
    const isOutOfBounds = index < 0 || index > options.length - 1;

    if (isOutOfBounds) {
      return {
        ...state,
        selectedIndex: null,
        isEditMode: true,
      };
    }

    return {
      ...state,
      isEditMode: true,
      selectedIndex: index,
    };
  };

  const { type } = action;
  switch (type) {
    case "focus":
      return { ...state, isEditMode: true, query: "" };
    case "clickOption":
      return {
        ...state,
        selectedIndex: null,
        query: getInputDisplayValue(action.option),
        isEditMode: false,
      };
    case "blur":
      return {
        ...state,
        isEditMode: false,
        selectedIndex: null,
      };
    case "arrowDown": {
      const { selectedIndex } = state;

      if (selectedIndex === null) {
        return arrowSelect(0, action.options);
      }

      return arrowSelect(selectedIndex + 1, action.options);
    }
    case "arrowUp": {
      const { selectedIndex } = state;

      if (selectedIndex === null) {
        return arrowSelect(action.options.length - 1, action.options);
      }

      return arrowSelect(selectedIndex - 1, action.options);
    }
    case "enter": {
      const { selectedIndex } = state;

      if (isNil(selectedIndex)) {
        return state;
      }

      const option = action.options[selectedIndex];

      if (!option) {
        return {
          ...state,
          selectedIndex: null,
        };
      }

      return {
        ...state,
        isEditMode: false,
        selectedIndex: null,
        query: getInputDisplayValue(option),
      };
    }
    case "input":
      return {
        ...state,
        query: action.value,
        isEditMode: true,
        selectedIndex: null,
      };
    default: {
      const exhaustivelyCheckedType: never = type;
      throw new Error(`No action defined for ${exhaustivelyCheckedType}`);
    }
  }
}
